import { useEffect, useRef, useState } from 'react'
import { Play, Pause, Volume2 } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'
import { formatDuration } from '../utils/scoring'

interface WaveformDisplayProps {
  file: File
  clickPositions?: number[]
  duration: number
}

export function WaveformDisplay({ file, clickPositions = [], duration }: WaveformDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!containerRef.current) return

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: 'hsl(220 20% 30%)', // forge-border-bright
      progressColor: 'hsl(38 92% 50%)', // forge-copper
      cursorColor: 'hsl(40 33% 96%)', // forge-text
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height: 80,
      normalize: true,
    })

    wavesurferRef.current = wavesurfer

    wavesurfer.loadBlob(file)

    wavesurfer.on('ready', () => {
      setIsReady(true)
    })

    wavesurfer.on('audioprocess', (time) => {
      setCurrentTime(time)
    })

    wavesurfer.on('play', () => setIsPlaying(true))
    wavesurfer.on('pause', () => setIsPlaying(false))
    wavesurfer.on('finish', () => setIsPlaying(false))

    return () => {
      wavesurfer.destroy()
    }
  }, [file])

  const togglePlayPause = () => {
    wavesurferRef.current?.playPause()
  }

  return (
    <div className="forge-card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-forge-copper" />
          <span className="text-sm font-medium text-white">Waveform</span>
        </div>
        <span className="text-xs text-forge-subtle font-mono">
          {formatDuration(currentTime)} / {formatDuration(duration)}
        </span>
      </div>

      {/* Waveform container */}
      <div className="relative rounded-lg overflow-hidden bg-forge-surface p-3">
        {/* Grid background */}
        <div className="absolute inset-0 forge-grid-bg opacity-30" />

        <div ref={containerRef} className="relative w-full" />

        {/* Click position markers */}
        {isReady && clickPositions.length > 0 && (
          <div className="absolute inset-0 pointer-events-none p-3">
            {clickPositions.slice(0, 20).map((pos, i) => (
              <div
                key={i}
                className="absolute top-3 bottom-3 w-0.5 bg-[hsl(var(--forge-danger)/0.7)]"
                style={{ left: `calc(12px + ${(pos / duration) * (100 - 24 / containerRef.current!.offsetWidth * 100)}%)` }}
                title={`Click at ${formatDuration(pos)}`}
              >
                {/* Glow effect */}
                <div className="absolute inset-0 w-2 -translate-x-[3px] bg-[hsl(var(--forge-danger)/0.3)] blur-sm" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center justify-center gap-4">
        <button
          onClick={togglePlayPause}
          disabled={!isReady}
          className={`
            relative flex items-center justify-center w-12 h-12 rounded-xl
            transition-all duration-200
            ${isReady
              ? 'bg-gradient-to-br from-[hsl(var(--forge-copper))] to-[hsl(var(--forge-copper-dim))] hover:scale-105 active:scale-100'
              : 'bg-forge-surface'
            }
          `}
        >
          {/* Glow effect */}
          {isReady && (
            <div className="absolute inset-0 bg-[hsl(var(--forge-copper))] rounded-xl blur-lg opacity-30" />
          )}

          {isPlaying ? (
            <Pause className="relative w-5 h-5 text-[hsl(var(--forge-bg-deep))]" />
          ) : (
            <Play className="relative w-5 h-5 text-[hsl(var(--forge-bg-deep))] ml-0.5" />
          )}
        </button>
      </div>

      {/* Click markers legend */}
      {clickPositions.length > 0 && (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-forge-muted">
          <div className="w-2 h-2 bg-[hsl(var(--forge-danger)/0.7)] rounded-sm" />
          <span>
            {clickPositions.length} click{clickPositions.length !== 1 ? 's' : ''} detected
          </span>
        </div>
      )}
    </div>
  )
}
